import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type { ClientLinkCreateResultV1 } from "@enrein/shared-contracts";
import type { StudioRuntimeConfig } from "./env.js";
import { clientLinkUrl } from "./client-link-url.js";

const SHARE_PREVIEW_ELIGIBLE_STATUSES = [
  "PREVIEW_READY",
  "CHANGE_REQUESTED",
  "APPROVAL_PENDING",
  "APPROVED",
  "PORTAL_ACTIVE",
  "SAFE_EDIT_ACTIVE",
  "HANDOFF_READY",
] as const;

export type SharePreviewResolution =
  | { readonly kind: "ready"; readonly url: string }
  | { readonly kind: "missing_token"; readonly clientLinkId: string }
  | { readonly kind: "not_shareable" };

export function canSharePreviewFromClientLinkStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }
  return (SHARE_PREVIEW_ELIGIBLE_STATUSES as readonly string[]).includes(status);
}

/** Matches Client Portal token minimum length in `location.ts`. */
export function shellPathHasClientLinkToken(shellPath: string): boolean {
  const hashIndex = shellPath.indexOf("#");
  if (hashIndex === -1) {
    return false;
  }
  const params = new URLSearchParams(shellPath.slice(hashIndex + 1));
  const token = params.get("token") ?? "";
  return token.length >= 32;
}

function resolveShellPathForClientLink(input: {
  readonly clientLinkId: string;
  readonly lastClientLink: ClientLinkCreateResultV1 | null;
  readonly storedSession: { readonly clientLinkId: string; readonly shellPath: string } | null;
}): string | null {
  if (input.lastClientLink?.clientLinkId === input.clientLinkId && shellPathHasClientLinkToken(input.lastClientLink.shellPath)) {
    return input.lastClientLink.shellPath;
  }
  if (
    input.storedSession?.clientLinkId === input.clientLinkId &&
    shellPathHasClientLinkToken(input.storedSession.shellPath)
  ) {
    return input.storedSession.shellPath;
  }
  return null;
}

export function resolveSharePreviewUrl(input: {
  readonly clientReview: ClientReviewStatusV1 | null;
  readonly lastClientLink: ClientLinkCreateResultV1 | null;
  readonly storedSession: { readonly clientLinkId: string; readonly shellPath: string } | null;
  readonly config: StudioRuntimeConfig;
}): SharePreviewResolution {
  const clientLink = input.clientReview?.clientLink;
  if (!clientLink || !input.clientReview?.latestPreview) {
    return { kind: "not_shareable" };
  }
  if (!canSharePreviewFromClientLinkStatus(clientLink.status)) {
    return { kind: "not_shareable" };
  }

  const shellPath = resolveShellPathForClientLink({
    clientLinkId: clientLink.clientLinkId,
    lastClientLink: input.lastClientLink,
    storedSession: input.storedSession,
  });
  if (!shellPath) {
    return { kind: "missing_token", clientLinkId: clientLink.clientLinkId };
  }

  return { kind: "ready", url: clientLinkUrl(input.config, shellPath) };
}
